import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  INSIGHT_GUIDANCE_CONFIG_NAME,
  useDeleteInsightConfigMutation,
  useLazyGetInsightConfigVersionsQuery,
  useQueryInsightConfigsQuery,
  useTypedSelector,
  type InsightConfigName,
  type InsightConfigRow,
} from '@circuitry-ai/doc-data';
import { getInsightCatalog } from '../insightAgentConfig';
import { channelVersion, isPublishedSkill, skillVersions, type GuidanceItem, type InsightCatalog, type SkillVersion } from './agentConfig';

/** Models, fixed rules, built-in guidance and the default config from the Insight agent service. */
export function useInsightCatalog(baseUrl: string) {
  const token = useTypedSelector((state) => state.auth.token);
  const [catalog, setCatalog] = useState<InsightCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token || !baseUrl) return undefined;
    const controller = new AbortController();
    setError(null);
    getInsightCatalog(baseUrl, token, controller.signal)
      .then(setCatalog)
      .catch((e: Error) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, [baseUrl, token, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { catalog, error, retry };
}

export interface LibraryItem extends GuidanceItem {
  /** `builtin:<name>`, `skill:<name>`, or the tenant row's config_id. */
  id: string;
  /** From the catalog, read-only: a built-in or a published skill. */
  builtin: boolean;
  /** A skill published from the skills repo (`skill:<name>`). */
  published: boolean;
  /** A published skill's channel version ('' when the catalog does not say). */
  version: string;
  /** A published skill's pinnable versions ([] when the catalog lists none). */
  versions: SkillVersion[];
  /** The active DOC row of a tenant item. */
  row?: InsightConfigRow<GuidanceItem>;
}

/** Built-in guidance (read-only) and the tenant's own items at their active versions. */
export function useGuidanceLibrary(catalog: InsightCatalog | null) {
  const token = useTypedSelector((state) => state.auth.token);
  const { data: rows = [], isFetching, error, refetch } = useQueryInsightConfigsQuery(
    { config_name: INSIGHT_GUIDANCE_CONFIG_NAME, is_active: true },
    { skip: !token, refetchOnMountOrArgChange: true }
  );

  const items = useMemo<LibraryItem[]>(() => {
    const builtins = (catalog?.builtin_guidance ?? []).map((item): LibraryItem => {
      const published = isPublishedSkill(item.id);
      return {
        id: item.id,
        name: item.name,
        description: item.description,
        body: item.body,
        builtin: true,
        published,
        version: published ? channelVersion(item) : '',
        versions: published ? skillVersions(item.versions) : [],
      };
    });
    const seen = new Set<string>();
    const tenant: LibraryItem[] = [];
    for (const row of rows as unknown as InsightConfigRow<GuidanceItem>[]) {
      if (seen.has(row.config_id)) continue; // newest first: a second active row is a stale duplicate
      seen.add(row.config_id);
      const config = (row.config ?? {}) as Partial<GuidanceItem>;
      tenant.push({
        id: row.config_id,
        name: config.name ?? '',
        description: config.description ?? '',
        body: config.body ?? '',
        builtin: false,
        published: false,
        version: '',
        versions: [],
        row,
      });
    }
    return [...builtins, ...tenant];
  }, [catalog, rows]);

  const index = useMemo(
    () => Object.fromEntries(items.map((item) => [item.id, { name: item.name, description: item.description, body: item.body }])),
    [items]
  );

  return { items, index, isFetching, error, refetch };
}

/** A readable message from an RTK Query error or a thrown Error. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const e = error as { message?: string; data?: { message?: string } } | undefined;
  return e?.message ?? e?.data?.message ?? fallback;
}

export const errorStatus = (error: unknown) => (error as { status?: number | string } | undefined)?.status;

/**
 * Deletes a configuration row and every version of it. DOC's DELETE soft-deletes one row, so the
 * inactive versions go first and the active one last: nothing is left to roll back to, and a
 * delete that stops half way leaves the item listed, so deleting again finishes it.
 */
export function useDeleteAllVersions() {
  const [getVersions] = useLazyGetInsightConfigVersionsQuery();
  const [deleteConfig] = useDeleteInsightConfigMutation();
  return useCallback(async (configId: string, configName: InsightConfigName, fallbackConfigurationId?: string) => {
    const versions = await getVersions({ config_id: configId, config_name: configName }).unwrap();
    const ids = versions.length > 0
      ? [...versions].sort((a, b) => Number(a.status === 'active') - Number(b.status === 'active')).map((v) => v.configuration_id)
      : [fallbackConfigurationId].filter((id): id is string => Boolean(id));
    for (const configuration_id of ids) {
      await deleteConfig({ configuration_id }).unwrap();
    }
  }, [deleteConfig, getVersions]);
}
